import { useLazyQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React, { useCallback, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { Navigate, useNavigate } from "react-router-dom"
import * as z from "zod"
import { gql } from "~/__generated__"
import {
  HandleAvailabilityStatus,
  UserStatus,
  VisibilityContactIndex,
  VisibilityWalletIndex,
} from "~/__generated__/graphql"
import { useViewerMaybe } from "~/auth/use-viewer"
import { contactsPath } from "~/common/paths"
import { useFormErrors } from "~/common/use-form-errors"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useInvitation, useAcceptInvitation } from "~/invitation/invitation-context"
import { HandleAvailabilityStatusDisplay } from "~/components/handle-availability-status"
import { TextField } from "~/fields/text-field"
import { Button } from "~/ui/button"
import { Form } from "~/ui/form"
import { Switch } from "~/ui/switch"
import { useToast } from "~/ui/use-toast"

const accountDetailsFormSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  handle: z
    .string()
    .transform((value) => (value.startsWith("@") ? value.slice(1) : value))
    .pipe(
      z
        .string()
        .min(3, { message: "Handle must be at least 3 characters" })
        .max(24, { message: "Handle must be 24 characters or less" })
        .regex(/^[a-zA-Z0-9_]+$/, {
          message: "Handle can only contain letters, numbers, and underscores",
        })
    ),
  visibilityWalletIndex: z.boolean().default(false),
  visibilityContactIndex: z.boolean().default(false),
})

type AccountDetailsFormValues = z.infer<typeof accountDetailsFormSchema>

const USER_UPDATE_MUTATION = gql(/* GraphQL */ `
  mutation UserUpdateAccountDetails($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      user {
        id
        firstName
        lastName
        handle
        userStatus
        visibilityWalletIndex
        visibilityContactIndex
      }
    }
  }
`)

const HANDLE_AVAILABLE_QUERY = gql(/* GraphQL */ `
  query HandleAvailable($handle: String!) {
    handleAvailable(handle: $handle)
  }
`)

export const OnboardingAccountDetailsScreen: React.FC = () => {
  const [userUpdate, userUpdateResult] = useSafeMutation(USER_UPDATE_MUTATION)
  const [checkHandle] = useLazyQuery(HANDLE_AVAILABLE_QUERY)
  const { invitation, hasInvitation, clearInvitation } = useInvitation()
  const [handleStatus, setHandleStatus] = useState<"checking" | HandleAvailabilityStatus | null>(
    null
  )
  const { toast } = useToast()
  const navigate = useNavigate()
  const {
    viewer,
    result: { refetch: refetchViewer },
  } = useViewerMaybe()

  const { acceptInvitation, loading: invitationLoading } = useAcceptInvitation({
    mode: "onboarding",
    onSuccess: (_, inviterName) => {
      clearInvitation()
      sessionStorage.setItem("welcomeInviterName", inviterName)
      navigate(contactsPath({}) + "?welcome=invitation")
    },
    onError: () => {
      toast({
        title: "Warning",
        description:
          "Account created but invitation could not be accepted. You can try accepting it later.",
        variant: "destructive",
      })
    },
  })

  // Helper function to normalize handle by removing leading '@'
  const normalizeHandle = useCallback((handle: string): string => {
    return handle.startsWith("@") ? handle.slice(1) : handle
  }, [])

  const form = useForm<AccountDetailsFormValues>({
    resolver: zodResolver(accountDetailsFormSchema),
    defaultValues: {
      firstName: viewer?.firstName || "",
      lastName: viewer?.lastName || "",
      handle: viewer?.handle ?? "",
      visibilityWalletIndex:
        viewer?.visibilityWalletIndex === VisibilityWalletIndex.Public || false,
      visibilityContactIndex:
        viewer?.visibilityContactIndex === VisibilityContactIndex.EmailAndPhone || false,
    },
  })

  useFormErrors(form.setError, userUpdateResult)

  // Watch handle value
  const handleValue = form.watch("handle")

  // Debounced handle availability check
  useEffect(() => {
    const normalizedHandle = normalizeHandle(handleValue || "")

    if (!normalizedHandle || normalizedHandle.length < 3) {
      setHandleStatus(null)
      return
    }

    const timeoutId = setTimeout(async () => {
      if (normalizedHandle === viewer?.handle) {
        setHandleStatus(HandleAvailabilityStatus.Available)
        return
      }

      setHandleStatus("checking")
      try {
        const result = await checkHandle({ variables: { handle: normalizedHandle } })
        const status = result.data?.handleAvailable
        setHandleStatus(status as HandleAvailabilityStatus)
      } catch {
        setHandleStatus(null)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [handleValue, checkHandle, viewer?.handle, normalizeHandle])

  // Redirect if user is already active
  if (viewer?.userStatus === UserStatus.Active) {
    return <Navigate to={contactsPath({})} replace />
  }

  const onSubmit = async (values: AccountDetailsFormValues) => {
    if (!viewer?.id) return

    const result = await userUpdate({
      variables: {
        input: {
          id: viewer.id,
          userInput: {
            firstName: values.firstName,
            lastName: values.lastName,
            handle: values.handle,
            userStatus: UserStatus.Active,
            visibilityWalletIndex: values.visibilityWalletIndex
              ? VisibilityWalletIndex.Public
              : VisibilityWalletIndex.Private,
            visibilityContactIndex: values.visibilityContactIndex
              ? VisibilityContactIndex.EmailAndPhone
              : VisibilityContactIndex.Private,
          },
        },
      },
    })

    if (result.errors) {
      return
    }

    await refetchViewer()

    // Accept invitation if present
    if (hasInvitation && invitation?.id) {
      await acceptInvitation()
    } else {
      toast({
        title: "Account Details Saved",
        description: "Your account has been successfully set up.",
        variant: "default",
      })
    }
  }

  return (
    <div>
      <div className="space-y-2">
        <h1 className="text-xs text-gray-500">Account Details</h1>
        <h2 className="text-lg font-medium text-gray-800">Choose your handle & create account</h2>
      </div>
      <hr className="mb-10 mt-6 border-t border-gray-200" />
      <div className="space-y-6">
        <p className="text-sm text-gray-600">
          Tell us a little bit about you and create a unique account handle.
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <TextField control={form.control} name="firstName" label="First Name" />
            <TextField control={form.control} name="lastName" label="Last Name" />
            <div className="space-y-2">
              <TextField
                control={form.control}
                name="handle"
                label="Handle"
                placeholder="@username"
              />

              <HandleAvailabilityStatusDisplay
                status={handleStatus}
                currentHandle={handleValue}
                originalHandle={viewer?.handle}
              />
              <p className="text-xs text-gray-500">Your unique username on the platform</p>
            </div>

            {/* Discoverability Section */}
            <div className="space-y-4 pt-8">
              <div className="space-y-2">
                <h1 className="text-xs text-gray-500">Discoverability</h1>
                <h2 className="text-lg font-medium text-gray-800">
                  Control how others can find and connect with you
                </h2>
              </div>
              <hr className="mb-10 mt-6 border-t border-gray-200" />

              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">Public Sharing</div>
                  <div className="text-xs text-gray-600">
                    Allow others to lookup my name based on my wallet address
                  </div>
                </div>
                <Switch
                  checked={form.watch("visibilityWalletIndex")}
                  onCheckedChange={(checked) => {
                    form.setValue("visibilityWalletIndex", checked)
                  }}
                />
              </div>

              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">Email or Phone Lookup</div>
                  <div className="text-xs text-gray-600">
                    Allow others to get my crypto address based on my email or phone number
                  </div>
                </div>
                <Switch
                  checked={form.watch("visibilityContactIndex")}
                  onCheckedChange={(checked) => {
                    form.setValue("visibilityContactIndex", checked)
                  }}
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={
                userUpdateResult.loading ||
                invitationLoading ||
                handleStatus === HandleAvailabilityStatus.Taken ||
                handleStatus === "checking" ||
                handleStatus === HandleAvailabilityStatus.InvalidFormat ||
                handleStatus === HandleAvailabilityStatus.TooShort ||
                handleStatus === HandleAvailabilityStatus.TooLong
              }
            >
              {userUpdateResult.loading || invitationLoading ? "Creating Account..." : "Let's go!"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}
